// How often the shell's one loop draws (#2569: the site lagged beside a video call, which needs the same graphics chip and CPU).
//  - while the learner moves (a pointer, a wheel, a key or a touch in the last 3 s, which also covers the flight a click starts),
//    a view draws at up to 60 frames a second: a 120 or 144 Hz screen gains little from more, and costs two or more times the work;
//  - while nothing is touched, a view's own slow motion (the lab's drifting camera, the deck's platters, a site's fans) draws at 30:
//    it moves the same way on the wall clock, with half the frames;
//  - a view that keeps every frame (the intro, cut to the music) is not capped while still;
//  - in light mode (src/quality.ts, #2569) every view draws at 30, moving or not.
// The frames left out are not counted by the quality governor (src/quality.ts): it judges the frames that draw, not the cap.
export const ACTIVE = 3000;   // ms after the last input that count as moving
export const MOVING = 60, STILL = 30;   // frames a second

export function createFrames() {
  let light = false, lastInput = -Infinity, then = -Infinity, ticks = 0, drawn = 0;
  return {
    /** The learner did something (the shell listens for pointers, wheels, keys and touches). */
    input(now: number) { lastInput = now; },
    /** Whether the learner moved in the last 3 s: work that can wait (a building's other rooms, #2589) waits while they move. */
    moving(now: number) { return now - lastInput < ACTIVE; },
    /** The frame rate at this moment: MOVING after input, STILL otherwise (or MOVING for a view that keeps every frame). */
    cap(now: number, smooth = false) { return light ? STILL : smooth || now - lastInput < ACTIVE ? MOVING : STILL; },
    /** Light mode (src/quality.ts): 30 frames a second, moving or not, the intro included. */
    light(on: boolean) { light = on; },
    /** Whether the loop's tick at `now` draws. Over time it draws at the cap on average, whatever the screen's rate: a 144 Hz
     *  screen draws every second or third tick for 60. A millisecond of slack keeps a 60 Hz screen's jittery ticks all drawn at 60. */
    due(now: number, smooth = false) {
      ticks++;
      const gap = 1000 / this.cap(now, smooth), elapsed = now - then;
      if (elapsed < gap - 1) return false;
      // the time over the gap carries to the next frame (so 144 Hz averages 60); a tick just short of a gap carries none, and a
      // long pause (a slow frame, a hidden tab) starts afresh
      const over = elapsed % gap;
      then = elapsed >= gap * 2 || over > gap - 1 ? now : now - over;
      drawn++; return true;
    },
    /** For ?perf and the tests: ticks seen and frames drawn so far. */
    info() { return { ticks, drawn }; }
  };
}
export type Frames = ReturnType<typeof createFrames>;

/** The page's one frame cap: the shell's loop asks it, and work that can wait asks whether the learner is moving. */
export const frames = createFrames();
